import type {
  Artefato,
  ArtefatoDiff,
  BuildDetalhe,
  BuildResumo,
  DiffAnterior,
  EventoBuild,
  EventoIncidente,
  IncidenteDetalhe,
  IncidenteResumo,
  Me,
  Acesso,
  Audiencia,
  EventoVisibilidade,
  Funcionalidade,
  Visibilidade,
  VisibilidadeFuncionalidade,
  VisibilidadeRamo,
  PaginaIncidentes,
  SeveridadeIncidente,
  StatusIncidente,
  TipoEventoIncidente,
  VersaoDoIncidente,
  Painel,
  PaginaBuilds,
  CapabilitiesDoUsuario,
  EventoCapability,
  Ramo,
  Resumo,
  UsuarioComCapabilities,
  UsuarioKeycloak,
  StatusBuild,
  TipoArtefato,
  TipoEvento,
} from '@/data/types';

/**
 * Normalização das respostas da API.
 *
 * Não renomeia nada (o contrato já é o vocabulário da tela): só garante os
 * tipos. Campo ausente vira `null`/lista vazia em vez de `undefined`
 * espalhado pelas telas, número que veio como string vira número, e status
 * desconhecido cai em `nao_homologado` — a tela nunca quebra por um campo novo ou
 * faltando, e um valor inesperado não vira "homologado" por engano.
 */

type Bruto = Record<string, unknown>;

function obj(valor: unknown): Bruto {
  return valor && typeof valor === 'object' && !Array.isArray(valor) ? (valor as Bruto) : {};
}

function str(valor: unknown): string | null {
  if (typeof valor === 'string') return valor.trim() === '' ? null : valor;
  if (typeof valor === 'number') return String(valor);
  return null;
}

function num(valor: unknown): number | null {
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
  if (typeof valor === 'string' && valor.trim() !== '' && Number.isFinite(Number(valor))) return Number(valor);
  return null;
}

function lista(valor: unknown): unknown[] {
  return Array.isArray(valor) ? valor : [];
}

const STATUS_VALIDOS: StatusBuild[] = ['nao_homologado', 'homologado', 'reprovado'];

export function toStatus(valor: unknown): StatusBuild {
  return STATUS_VALIDOS.includes(valor as StatusBuild) ? (valor as StatusBuild) : 'nao_homologado';
}

function toStatusOuNulo(valor: unknown): StatusBuild | null {
  return STATUS_VALIDOS.includes(valor as StatusBuild) ? (valor as StatusBuild) : null;
}

/** Tipo sempre preenchido pelo contrato; ausente cai em `exe`, o caso de quase todo artefato. */
export function toTipoArtefato(valor: unknown): TipoArtefato {
  return str(valor)?.toLowerCase() ?? 'exe';
}

/** Ramo derivado do 2º segmento da versão (`2.2602.78.0` -> `2602`) — mesma regra da ingestão. */
export function ramoDaVersao(versao: string): string {
  return versao.split('.')[1] ?? '';
}

const SEVERIDADES_VALIDAS: SeveridadeIncidente[] = ['baixa', 'media', 'alta', 'critica'];
const STATUS_INCIDENTE_VALIDOS: StatusIncidente[] = ['aberto', 'mitigado', 'resolvido'];

function toSeveridadeOuNulo(valor: unknown): SeveridadeIncidente | null {
  return SEVERIDADES_VALIDAS.includes(valor as SeveridadeIncidente) ? (valor as SeveridadeIncidente) : null;
}

/** Severidade desconhecida cai em `media`: nem esconde nem inflaciona o incidente. */
export function toSeveridade(valor: unknown): SeveridadeIncidente {
  return toSeveridadeOuNulo(valor) ?? 'media';
}

/** Status desconhecido cai em `aberto`: um incidente nunca "some" por um valor novo da API. */
export function toStatusIncidente(valor: unknown): StatusIncidente {
  return STATUS_INCIDENTE_VALIDOS.includes(valor as StatusIncidente) ? (valor as StatusIncidente) : 'aberto';
}

export function toBuildResumo(valor: unknown): BuildResumo {
  const bruto = obj(valor);
  const links = obj(bruto.links);
  const jenkins = bruto.jenkins ? obj(bruto.jenkins) : null;
  const versao = str(bruto.versao) ?? '';
  return {
    id: num(bruto.id) ?? 0,
    produto: str(bruto.produto) ?? 'instalador-erp',
    branch: str(bruto.branch) ?? '',
    ramo: str(bruto.ramo) ?? ramoDaVersao(versao),
    versao,
    status: toStatus(bruto.status),
    status_motivo: str(bruto.status_motivo),
    status_por: str(bruto.status_por),
    status_em: str(bruto.status_em),
    publicado_em: str(bruto.publicado_em) ?? '',
    links: { instalador: str(links.instalador), instalador_cliente: str(links.instalador_cliente) },
    total_artefatos: num(bruto.total_artefatos) ?? 0,
    no_painel: bruto.no_painel === true,
    incidentes_abertos: num(bruto.incidentes_abertos) ?? 0,
    incidente_severidade_max: toSeveridadeOuNulo(bruto.incidente_severidade_max),
    jenkins: jenkins ? { job: str(jenkins.job), build: num(jenkins.build), url: str(jenkins.url) } : null,
  };
}

export function toArtefato(valor: unknown): Artefato {
  const bruto = obj(valor);
  return {
    grupo: str(bruto.grupo) ?? 'br.com.nasajon',
    artefato: str(bruto.artefato) ?? '',
    tipo: toTipoArtefato(bruto.tipo),
    versao: str(bruto.versao) ?? '',
  };
}

function toArtefatoDiff(valor: unknown): ArtefatoDiff {
  const bruto = obj(valor);
  return { artefato: str(bruto.artefato) ?? '', tipo: toTipoArtefato(bruto.tipo), versao: str(bruto.versao) ?? '' };
}

export function toDiff(valor: unknown): DiffAnterior {
  const bruto = obj(valor);
  return {
    versao_anterior: str(bruto.versao_anterior),
    alterados: lista(bruto.alterados).map((item) => {
      const b = obj(item);
      return { artefato: str(b.artefato) ?? '', tipo: toTipoArtefato(b.tipo), de: str(b.de) ?? '', para: str(b.para) ?? '' };
    }),
    adicionados: lista(bruto.adicionados).map(toArtefatoDiff),
    removidos: lista(bruto.removidos).map(toArtefatoDiff),
  };
}

const TIPOS_EVENTO: TipoEvento[] = ['publicado', 'republicado', 'status', 'painel'];

function toEvento(valor: unknown): EventoBuild {
  const bruto = obj(valor);
  return {
    id: num(bruto.id) ?? 0,
    tipo: TIPOS_EVENTO.includes(bruto.tipo as TipoEvento) ? (bruto.tipo as TipoEvento) : 'status',
    status_anterior: toStatusOuNulo(bruto.status_anterior),
    status_novo: toStatusOuNulo(bruto.status_novo),
    motivo: str(bruto.motivo),
    usuario: str(bruto.usuario) ?? 'sistema',
    criado_em: str(bruto.criado_em) ?? '',
  };
}

export function toBuildDetalhe(valor: unknown): BuildDetalhe {
  const bruto = obj(valor);
  const elegivel = obj(bruto.elegivel_painel);
  const artefatos = lista(bruto.artefatos).map(toArtefato);
  const resumo = toBuildResumo(bruto);
  return {
    ...resumo,
    total_artefatos: resumo.total_artefatos || artefatos.length,
    artefatos,
    eventos: lista(bruto.eventos)
      .map(toEvento)
      .sort((a, b) => b.criado_em.localeCompare(a.criado_em)),
    diff_anterior: toDiff(bruto.diff_anterior),
    elegivel_painel: { ok: elegivel.ok === true, motivo: str(elegivel.motivo) },
    incidentes: lista(bruto.incidentes).map(toIncidenteResumo),
  };
}

export function toPagina(valor: unknown): PaginaBuilds {
  const bruto = obj(valor);
  return { items: lista(bruto.items).map(toBuildResumo), next_cursor: str(bruto.next_cursor) };
}

export function toResumo(valor: unknown): Resumo {
  const bruto = obj(valor);
  const painel = obj(bruto.painel);
  return {
    total_builds: num(bruto.total_builds) ?? 0,
    ultimas_24h: num(bruto.ultimas_24h) ?? 0,
    nao_homologadas: num(bruto.nao_homologadas) ?? 0,
    incidentes_abertos: num(bruto.incidentes_abertos) ?? 0,
    painel: { versao: str(painel.versao), desde: str(painel.desde) },
  };
}

export function toRamo(valor: unknown): Ramo {
  const bruto = obj(valor);
  return {
    ramo: str(bruto.ramo) ?? '',
    branches: lista(bruto.branches).map(str).filter((item): item is string => item !== null),
    total: num(bruto.total) ?? 0,
    nao_homologadas: num(bruto.nao_homologadas) ?? 0,
    homologados: num(bruto.homologados) ?? 0,
    reprovados: num(bruto.reprovados) ?? 0,
    ultima_versao: str(bruto.ultima_versao),
    ultima_publicacao: str(bruto.ultima_publicacao),
    ultima_homologada: str(bruto.ultima_homologada),
  };
}

export function toPainel(valor: unknown): Painel {
  const bruto = obj(valor);
  return {
    modo: bruto.modo === 'atendimento' ? 'atendimento' : 'local',
    versao_atual: str(bruto.versao_atual),
    atualizado_em: str(bruto.atualizado_em),
    build: bruto.build ? toBuildResumo(bruto.build) : null,
    historico: lista(bruto.historico).map((item) => {
      const b = obj(item);
      return {
        versao: str(b.versao) ?? '',
        versao_anterior: str(b.versao_anterior),
        usuario: str(b.usuario) ?? 'sistema',
        criado_em: str(b.criado_em) ?? '',
      };
    }),
  };
}

export function toMe(valor: unknown): Me {
  const bruto = obj(valor);
  const acessoBruto = obj(bruto.acesso);
  // Campo ausente (API antiga) = liberado: a API é quem barra, a tela só esconde.
  const flag = (chave: string) => (typeof acessoBruto[chave] === 'boolean' ? (acessoBruto[chave] as boolean) : true);
  const acesso: Acesso = { feed: flag('feed'), ramos: flag('ramos'), incidentes: flag('incidentes'), painel: flag('painel') };
  return {
    usuario: str(bruto.usuario) ?? '',
    nome: str(bruto.nome) ?? str(bruto.usuario) ?? '',
    capabilities: lista(bruto.capabilities).map(str).filter((item): item is string => item !== null),
    colaborador: bruto.colaborador === true,
    acesso,
  };
}

const AUDIENCIAS_VALIDAS: Audiencia[] = ['publico', 'colaboradores', 'oculto'];
const FUNCIONALIDADES_VALIDAS: Funcionalidade[] = ['feed', 'ramos', 'incidentes', 'painel'];

/** Audiência desconhecida cai em `oculto`: o valor inesperado nunca vira "mais aberto". */
export function toAudiencia(valor: unknown): Audiencia {
  return AUDIENCIAS_VALIDAS.includes(valor as Audiencia) ? (valor as Audiencia) : 'oculto';
}

export function toVisibilidadeFuncionalidade(valor: unknown): VisibilidadeFuncionalidade {
  const bruto = obj(valor);
  const f = str(bruto.funcionalidade);
  return {
    funcionalidade: FUNCIONALIDADES_VALIDAS.includes(f as Funcionalidade) ? (f as Funcionalidade) : 'feed',
    audiencia: toAudiencia(bruto.audiencia),
    atualizado_por: str(bruto.atualizado_por),
    atualizado_em: str(bruto.atualizado_em),
  };
}

export function toVisibilidadeRamo(valor: unknown): VisibilidadeRamo {
  const bruto = obj(valor);
  return {
    produto: str(bruto.produto) ?? '',
    ramo: str(bruto.ramo) ?? '',
    audiencia: toAudiencia(bruto.audiencia),
    total_builds: num(bruto.total_builds) ?? 0,
    atualizado_por: str(bruto.atualizado_por),
    atualizado_em: str(bruto.atualizado_em),
  };
}

export function toVisibilidade(valor: unknown): Visibilidade {
  const bruto = obj(valor);
  return {
    funcionalidades: lista(bruto.funcionalidades).map(toVisibilidadeFuncionalidade),
    ramos: lista(bruto.ramos).map(toVisibilidadeRamo),
    audiencias: lista(bruto.audiencias).map(toAudiencia),
  };
}

export function toEventoVisibilidade(valor: unknown): EventoVisibilidade {
  const bruto = obj(valor);
  return {
    id: num(bruto.id) ?? 0,
    alvo: str(bruto.alvo) ?? '',
    audiencia_anterior: bruto.audiencia_anterior == null ? null : toAudiencia(bruto.audiencia_anterior),
    audiencia_nova: toAudiencia(bruto.audiencia_nova),
    usuario: str(bruto.usuario) ?? '',
    criado_em: str(bruto.criado_em) ?? '',
  };
}


/* ------------------------------- Incidentes ------------------------------- */

export function toIncidenteResumo(valor: unknown): IncidenteResumo {
  const bruto = obj(valor);
  return {
    id: num(bruto.id) ?? 0,
    produto: str(bruto.produto) ?? 'instalador-erp',
    titulo: str(bruto.titulo) ?? '(sem título)',
    severidade: toSeveridade(bruto.severidade),
    status: toStatusIncidente(bruto.status),
    link: str(bruto.link),
    corrigido_em: str(bruto.corrigido_em),
    criado_por: str(bruto.criado_por) ?? 'sistema',
    criado_em: str(bruto.criado_em) ?? '',
    atualizado_em: str(bruto.atualizado_em) ?? str(bruto.criado_em) ?? '',
    resolvido_em: str(bruto.resolvido_em),
    resolvido_por: str(bruto.resolvido_por),
    total_versoes: num(bruto.total_versoes) ?? 0,
  };
}

function toVersaoDoIncidente(valor: unknown): VersaoDoIncidente {
  const bruto = obj(valor);
  const versao = str(bruto.versao) ?? '';
  return {
    versao,
    branch: str(bruto.branch) ?? '',
    ramo: str(bruto.ramo) ?? ramoDaVersao(versao),
    status: toStatus(bruto.status),
    adicionado_por: str(bruto.adicionado_por) ?? 'sistema',
    adicionado_em: str(bruto.adicionado_em) ?? '',
  };
}

const TIPOS_EVENTO_INCIDENTE: TipoEventoIncidente[] = ['criado', 'editado', 'status', 'versao_adicionada', 'versao_removida'];

function toEventoIncidente(valor: unknown): EventoIncidente {
  const bruto = obj(valor);
  return {
    id: num(bruto.id) ?? 0,
    tipo: TIPOS_EVENTO_INCIDENTE.includes(bruto.tipo as TipoEventoIncidente)
      ? (bruto.tipo as TipoEventoIncidente)
      : 'editado',
    detalhe: obj(bruto.detalhe),
    usuario: str(bruto.usuario) ?? 'sistema',
    criado_em: str(bruto.criado_em) ?? '',
  };
}

export function toIncidenteDetalhe(valor: unknown): IncidenteDetalhe {
  const bruto = obj(valor);
  const resumo = toIncidenteResumo(bruto);
  const versoes = lista(bruto.versoes).map(toVersaoDoIncidente);
  return {
    ...resumo,
    total_versoes: resumo.total_versoes || versoes.length,
    // Descrição pode ser vazia (o contrato permite): `str` a transformaria em null.
    descricao: typeof bruto.descricao === 'string' ? bruto.descricao : '',
    versoes,
    eventos: lista(bruto.eventos)
      .map(toEventoIncidente)
      .sort((a, b) => b.criado_em.localeCompare(a.criado_em) || b.id - a.id),
  };
}

export function toPaginaIncidentes(valor: unknown): PaginaIncidentes {
  const bruto = obj(valor);
  return { items: lista(bruto.items).map(toIncidenteResumo), next_cursor: str(bruto.next_cursor) };
}

/* ---------------------- Administração de capabilities ---------------------- */

function listaDeTexto(valor: unknown): string[] {
  return [...new Set(lista(valor).filter((item): item is string => typeof item === 'string' && item.trim() !== ''))];
}

/** Sem `nome` na conta do Keycloak, a tela mostra o username (ou o e-mail) no lugar. */
export function toUsuarioKeycloak(valor: unknown): UsuarioKeycloak {
  const bruto = obj(valor);
  const email = str(bruto.email) ?? '';
  const username = str(bruto.username) ?? email;
  return {
    keycloak_user_id: str(bruto.keycloak_user_id) ?? '',
    email,
    username,
    nome: str(bruto.nome) ?? username,
  };
}

export function toUsuarioComCapabilities(valor: unknown): UsuarioComCapabilities {
  return { ...toUsuarioKeycloak(valor), capabilities: listaDeTexto(obj(valor).capabilities) };
}

export function toCapabilitiesDoUsuario(valor: unknown): CapabilitiesDoUsuario {
  const bruto = obj(valor);
  return {
    keycloak_user_id: str(bruto.keycloak_user_id) ?? '',
    email: str(bruto.email) ?? '',
    capabilities: listaDeTexto(bruto.capabilities),
  };
}

export function toEventoCapability(valor: unknown): EventoCapability {
  const bruto = obj(valor);
  return {
    id: num(bruto.id) ?? 0,
    ator: str(bruto.ator) ?? 'desconhecido',
    alvo_id: str(bruto.alvo_id) ?? '',
    alvo_email: str(bruto.alvo_email),
    capability: str(bruto.capability) ?? '',
    // Valor desconhecido cai em `revogar`: nunca mostra uma concessão que não houve.
    acao: bruto.acao === 'conceder' ? 'conceder' : 'revogar',
    criado_em: str(bruto.criado_em) ?? '',
  };
}
